import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingCart, ArrowRight } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { useSiteConfig } from "../context/ConfigContext";

const METHOD_LABELS = { mpesa: "M-Pesa", card: "Card", snippe: "Snippe" };

export default function Cart() {
  const { user } = useAuth();
  const { t } = useLang();
  const { fmt } = useCurrency();
  const { refreshCart } = useCart();
  const { features } = useSiteConfig();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState("");
  const [method, setMethod] = useState("mpesa");
  const [note, setNote] = useState("");
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  const load = () =>
    api("/cart", { auth: true })
      .then((c) => {
        setCart(c);
        refreshCart();
      })
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    if (!user) {
      navigate("/login?next=/cart");
      return;
    }
    load();
    api("/me/addresses", { auth: true })
      .then((list) => {
        setAddresses(list);
        const def = list.find((a) => a.is_default) || list[0];
        if (def) setSelected(def.id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (features.cart === false)
    return (
      <div data-testid="cart-off" className="max-w-xl mx-auto px-4 py-24 text-center text-sm text-slate-500">{t("empty_cart_sub")}</div>
    );

  if (!user) return null;

  const itemKey = (it) => it.key || it.id || it.product_id;

  const setQty = async (it, qty) => {
    if (qty < 1) return;
    try {
      await api(`/cart/items?key=${encodeURIComponent(itemKey(it))}`, {
        method: "PATCH",
        auth: true,
        body: { quantity: qty },
      });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const removeItem = async (it) => {
    try {
      await api(`/cart/items?key=${encodeURIComponent(itemKey(it))}`, { method: "DELETE", auth: true });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const checkout = async () => {
    const a = addresses.find((x) => x.id === selected);
    if (!a) {
      toast.error(t("delivery_addr"));
      return;
    }
    setPlacing(true);
    try {
      const res = await api("/cart/checkout", {
        method: "POST",
        auth: true,
        body: {
          payment_method: method,
          delivery: { name: a.name, phone: a.phone, city: a.city, region: a.region, country: a.country, address: a.address, landmark: a.landmark },
          note,
        },
      });
      setDone(res);
      refreshCart();
      toast.success(t("order_placed"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPlacing(false);
    }
  };

  if (done)
    return (
      <div data-testid="cart-success" className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-100 text-3xl text-teal-700">✓</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">{t("order_placed")}</h1>
        <p className="mt-2 text-sm text-slate-600">
          {(done.group_id || done.gid || done.id || "").slice(-8).toUpperCase()}
        </p>
        <Link
          data-testid="cart-success-orders"
          to="/account"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-teal-700 px-6 py-3 text-sm font-bold text-white hover:bg-teal-800 transition"
        >
          {t("tab_orders")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );

  const items = cart?.items || [];

  return (
    <div data-testid="cart-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-700/10 text-teal-700">
          <ShoppingCart className="h-6 w-6" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{t("cart_t")}</h1>
      </div>

      {cart === null ? (
        <p data-testid="cart-loading" className="py-16 text-center text-sm text-slate-500">...</p>
      ) : items.length === 0 ? (
        <div data-testid="cart-empty" className="mt-8 rounded-2xl border border-dashed border-slate-300 py-16 text-center">
          <p className="font-heading text-xl font-semibold text-slate-800">{t("empty_cart")}</p>
          <p className="mt-1 text-sm text-slate-500">{t("empty_cart_sub")}</p>
          <Link
            data-testid="cart-browse-link"
            to="/products"
            className="mt-6 inline-block rounded-full bg-teal-700 px-6 py-2.5 text-sm font-bold text-white hover:bg-teal-800 transition"
          >
            {t("nav_products")}
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {items.map((it) => (
              <div key={itemKey(it)} data-testid={`cart-item-${itemKey(it)}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-teal-50">
                  {(it.image || it.thumb) && <img src={mediaUrl(it.thumb || it.image)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{it.title || it.product_title}</p>
                  <p className="text-xs text-slate-500">
                    {[it.color, it.size, it.volume].filter(Boolean).join(" · ")}
                  </p>
                  <p className="mt-1 text-sm font-bold text-teal-700">{fmt(it.price ?? it.unit_price)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`cart-minus-${itemKey(it)}`}
                    onClick={() => setQty(it, (it.quantity || 1) - 1)}
                    className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:border-teal-400"
                    aria-label="Decrease"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span data-testid={`cart-qty-${itemKey(it)}`} className="w-8 text-center text-sm font-bold">{it.quantity}</span>
                  <button
                    data-testid={`cart-plus-${itemKey(it)}`}
                    onClick={() => setQty(it, (it.quantity || 1) + 1)}
                    className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:border-teal-400"
                    aria-label="Increase"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button
                  data-testid={`cart-remove-${itemKey(it)}`}
                  onClick={() => removeItem(it)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 transition"
                  aria-label="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-teal-900/10 bg-white p-6 h-fit">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">{t("subtotal")}</span>
              <span data-testid="cart-subtotal" className="font-extrabold text-teal-700 text-lg">{fmt(cart.subtotal)}</span>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-slate-700">{t("delivery_addr")}</label>
              {addresses.length === 0 ? (
                <Link data-testid="cart-add-address" to="/account" className="mt-2 block rounded-xl border border-dashed border-teal-300 px-4 py-3 text-center text-xs font-semibold text-teal-700 hover:bg-teal-50">
                  {t("add_address")}
                </Link>
              ) : (
                <select
                  data-testid="cart-address-select"
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500"
                >
                  {addresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label || a.name} — {a.city}{a.country ? `, ${a.country}` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="mt-3">
              <label className="text-sm font-medium text-slate-700">{t("payment_method")}</label>
              <select
                data-testid="cart-payment-select"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500"
              >
                {["mpesa", "card", "snippe"].map((m) => (
                  <option key={m} value={m}>{METHOD_LABELS[m]}</option>
                ))}
              </select>
            </div>
            <input
              data-testid="cart-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("note_supplier")}
              className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal-500"
            />
            <button
              data-testid="cart-checkout-button"
              onClick={checkout}
              disabled={placing}
              className="mt-4 w-full rounded-xl bg-teal-700 py-3 text-sm font-bold text-white shadow-lg shadow-teal-700/25 hover:bg-teal-800 transition active:scale-95 disabled:opacity-60"
            >
              {placing ? t("placing") : t("checkout")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
